/**
 * The one NPS rule (plots plan P), for the surfaces that still tally NPS answers
 * in the browser (quick study) until their Python producer ships.
 *
 * Same policy and same order of operations as Python
 * (`rating_statistics.compute_rating_statistics` with `nps_like`, and
 * `quick_study/analysis.py` `_nps_read`): each RAW answer is classified by the
 * generated NPS policy (promoter >= 9, detractor <= 6, else passive; never
 * pre-rounded, so 8.6 is a passive), shares stay unrounded, the score is the
 * difference of the unrounded shares, and every printed figure is rounded once,
 * half away from zero.
 */

import { NPS_POLICY } from '@digital-twins/shared-types'

import { roundHalfAwayFromZero } from '../../foundations/producer-rounding'

export type NpsGroup = 'detractors' | 'passives' | 'promoters'

export function npsGroupOf(value: number): NpsGroup {
  if (value >= NPS_POLICY.favorableMinimum) {
    return 'promoters'
  }
  if (value <= NPS_POLICY.unfavorableMaximum) {
    return 'detractors'
  }
  return 'passives'
}

export interface NpsSplit {
  detractors: number
  passives: number
  promoters: number
  n: number
  detractorPct: number
  passivePct: number
  promoterPct: number
  nps: number
}

/** Counts, printed shares and the score for the in-range numeric answers, or null. */
export function npsSplit(values: Array<unknown>): NpsSplit | null {
  let detractors = 0
  let passives = 0
  let promoters = 0
  for (const value of values) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      continue
    }
    if (value < NPS_POLICY.minimum || value > NPS_POLICY.maximum) {
      continue
    }
    const group = npsGroupOf(value)
    if (group === 'promoters') {
      promoters += 1
    } else if (group === 'passives') {
      passives += 1
    } else {
      detractors += 1
    }
  }
  const n = detractors + passives + promoters
  if (!n) {
    return null
  }
  const promoterShare = (100 * promoters) / n
  const detractorShare = (100 * detractors) / n
  return {
    detractors,
    passives,
    promoters,
    n,
    detractorPct: roundHalfAwayFromZero(detractorShare),
    passivePct: roundHalfAwayFromZero((100 * passives) / n),
    promoterPct: roundHalfAwayFromZero(promoterShare),
    nps: roundHalfAwayFromZero(promoterShare - detractorShare),
  }
}
